import * as React from 'react'
import { useNavigate, useParams } from 'react-router'
import {
	Box,
	Button,
	CircularProgress,
	Stack,
	Typography,
} from '@mui/material'
import { ArrowBack } from '@mui/icons-material'
import { StatusCard } from '../../components/status-card'
import { usePageTitle } from '../../hooks'
import { useGetFeedQuery } from '../../graphql'
import { ManageForm } from '../../components/manage-form/manage-form'
import {
	trackAnalyticEvent,
	appendJDLinkToHeapLocation,
	ANALYTICS_LOCATIONS,
	LANDING_ROUTE,
} from '../../utils'
import { useModal } from '../../providers'
import {
	BreadcrumbsLink,
	CenteredContainer,
	ManageTitle,
	PageSection,
	PageWrapper,
} from './manage.styles'

export const ManagePage: React.FC = () => {
	usePageTitle( 'Manage' )

	const { id } = useParams<{ id: string }>()
	const { data: feedData, isLoading } = useGetFeedQuery( { feedId: id ?? '' } )
	const integrationName = feedData?.feed?.feed?.integration?.name
	const [ isFormDirty, setIsFormDirty ] = React.useState( false )
	const { showModal, hideModal } = useModal()
	const navigate = useNavigate()

	if ( isLoading ) {
		return (
			<PageWrapper height={'90vh'}>
				<Stack height={'100%'} width={'100%'} justifyContent={'center'} alignItems={'center'}>
					<CircularProgress aria-label={'loading'} />
				</Stack>
			</PageWrapper>
		)
	}

	if ( !feedData?.feed?.feed ) {
		return (
			<PageWrapper>
				<CenteredContainer>
					<Typography variant="h3" color="text.secondary">
						Feed not found
					</Typography>
					<Typography variant="body1" color="text.secondary">
						The feed you&apos;re looking for could not be found.
					</Typography>
					<Button
						variant="contained"
						color="primary"
						startIcon={<ArrowBack />}
						onClick={() => navigate( LANDING_ROUTE )}
					>
						Back to Integrations
					</Button>
				</CenteredContainer>
			</PageWrapper>
		)
	}

	const onBreadcrumbClick = () => {
		if ( isFormDirty ) {
			// Show unsaved changes modal
			showModal( {
				title: 'Unsaved Changes',
				headerText: 'Integration has unsaved changes',
				content: 'Changes to this integration will be lost. Are you sure you want to leave?',
				buttons: [
					{ label: 'No, stay', variant: 'text', color: 'primary', onClick: () => hideModal() },
					{ label: 'Yes, leave',
						variant: 'contained',
						color: 'primary',
						onClick: () => {
							trackAnalyticEvent(
								ANALYTICS_LOCATIONS.MANAGE_PAGE,
								'Click',
								appendJDLinkToHeapLocation( 'Bread Crumb Back', integrationName ),
							)
							setIsFormDirty( false )
							navigate( LANDING_ROUTE )
						} },
				],
			} )
		} else {
			trackAnalyticEvent(
				ANALYTICS_LOCATIONS.MANAGE_PAGE,
				'Click',
				appendJDLinkToHeapLocation( 'Bread Crumb Back', integrationName ),
			)
			navigate( LANDING_ROUTE )
		}
	}

	return (
		<PageWrapper>
			<PageSection spacing={4}>
				<Box>
					<BreadcrumbsLink
						underline="hover"
						color={'primary.main'}
						onClick={onBreadcrumbClick}
					>
						<ArrowBack fontSize="inherit" />
						<Button variant="text" aria-label="Integrations">
							<Typography variant="body1" color="primary">
									Integrations
							</Typography>
						</Button>
					</BreadcrumbsLink>
					<ManageTitle>
				Manage {integrationName} Integration
					</ManageTitle>
				</Box>
				<StatusCard onLinkClick={( title: string ) => {
					trackAnalyticEvent(
						ANALYTICS_LOCATIONS.MANAGE_PAGE,
						'Click',
						appendJDLinkToHeapLocation( title, integrationName ),
					)
				}} />
				<ManageForm isFormDirty={isFormDirty} setIsFormDirty={setIsFormDirty} />
			</PageSection>
		</PageWrapper>
	)
}
